import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Check,
  Loader2,
  Megaphone,
  Search,
  Shirt,
  UserCheck,
  X,
  Layers,
  Clock,
} from 'lucide-react';
import { getPublicVendors } from '../api/user.api';
import { getUserProfile } from '../api';
import {
  createPromotionRequest,
  getPendingPromotionRequests,
  getMyPromoters,
  getPromotedVendors,
  removeApprovedPromoter,
  updatePromotionRequestStatus,
} from '../api/promotionRequest.api';

const TABS = [
  { id: 'send', label: 'Promotion request', shortLabel: 'Request', icon: Megaphone },
  { id: 'pending', label: 'Pending requests', shortLabel: 'Pending', icon: Clock },
  { id: 'promoters', label: 'My promoters', shortLabel: 'Promoters', icon: UserCheck },
  { id: 'suppliers', label: 'My suppliers', shortLabel: 'Suppliers', icon: Layers },
];

const typeLabel = (t) => (t === 'body_cuts' ? 'Body cuts' : 'Fabrics');

const PromotersView = ({ setActiveView, showNotification }) => {
  const [mainTab, setMainTab] = useState('send');
  const [myUserId, setMyUserId] = useState(null);

  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [promotionType, setPromotionType] = useState('fabrics');
  const [sendLoading, setSendLoading] = useState(false);

  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [actionId, setActionId] = useState(null);

  const [promoters, setPromoters] = useState([]);
  const [promotersLoading, setPromotersLoading] = useState(false);
  const [removePromoterId, setRemovePromoterId] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  const loadProfile = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await getUserProfile(token);
      const u = res?.data?.data?.user;
      const uid = u?._id || u?.id;
      if (uid) setMyUserId(String(uid));
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const fetchVendors = useCallback(async () => {
    setVendorsLoading(true);
    try {
      const data = await getPublicVendors({ page: 1, limit: 50, search });
      let list = data.vendors || [];
      if (myUserId) {
        list = list.filter((v) => String(v.id || v._id) !== myUserId);
      }
      setVendors(list);
    } catch (e) {
      console.error(e);
      showNotification?.(e.response?.data?.message || 'Could not load vendors', 'error');
      setVendors([]);
    } finally {
      setVendorsLoading(false);
    }
  }, [search, showNotification, myUserId]);

  useEffect(() => {
    if (mainTab !== 'send') return;
    const t = setTimeout(() => {
      setSearch((prev) => {
        const next = searchDraft.trim();
        return prev === next ? prev : next;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [searchDraft, mainTab]);

  useEffect(() => {
    if (mainTab !== 'send') return;
    fetchVendors();
  }, [mainTab, fetchVendors]);

  const loadPending = useCallback(async () => {
    if (!localStorage.getItem('token')) {
      setIncoming([]);
      setOutgoing([]);
      return;
    }
    setPendingLoading(true);
    try {
      const data = await getPendingPromotionRequests();
      setIncoming(data.incoming || []);
      setOutgoing(data.outgoing || []);
    } catch (e) {
      console.error(e);
      showNotification?.(e.response?.data?.message || 'Could not load pending requests', 'error');
    } finally {
      setPendingLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const loadPromotersList = useCallback(async () => {
    setPromotersLoading(true);
    try {
      const list = await getMyPromoters();
      setPromoters(list);
    } catch (e) {
      console.error(e);
      showNotification?.(e.response?.data?.message || 'Could not load promoters', 'error');
      setPromoters([]);
    } finally {
      setPromotersLoading(false);
    }
  }, [showNotification]);

  const loadSuppliersList = useCallback(async () => {
    setSuppliersLoading(true);
    try {
      const list = await getPromotedVendors();
      setSuppliers(list || []);
    } catch (e) {
      console.error(e);
      showNotification?.(e.response?.data?.message || 'Could not load suppliers', 'error');
      setSuppliers([]);
    } finally {
      setSuppliersLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    if (mainTab === 'pending') loadPending();
    if (mainTab === 'promoters') loadPromotersList();
    if (mainTab === 'suppliers') loadSuppliersList();
  }, [mainTab, loadPending, loadPromotersList, loadSuppliersList]);

  const handleSend = async () => {
    const vid = selectedVendor?.id || selectedVendor?._id;
    if (!vid) {
      showNotification?.('Select a vendor first', 'error');
      return;
    }
    setSendLoading(true);
    try {
      await createPromotionRequest({ toUserId: vid, promotionType });
      showNotification?.('Promotion request sent', 'success');
      setSelectedVendor(null);
      loadPending();
    } catch (e) {
      showNotification?.(e.response?.data?.message || 'Could not send request', 'error');
    } finally {
      setSendLoading(false);
    }
  };

  const respond = async (id, status) => {
    setActionId(id);
    try {
      await updatePromotionRequestStatus(id, status);
      showNotification?.(status === 'accepted' ? 'Request accepted' : 'Request rejected', 'success');
      await loadPending();
      if (status === 'accepted') loadPromotersList();
    } catch (e) {
      showNotification?.(e.response?.data?.message || 'Could not update request', 'error');
    } finally {
      setActionId(null);
    }
  };

  const vendorTitle = (v) => v.brandName || v.name || 'Vendor';
  const vendorSubtitle = (v) => [v.name, v.phoneDigits].filter(Boolean).join(' · ');

  const pendingCount = useMemo(() => incoming.length + outgoing.length, [incoming.length, outgoing.length]);

  return (
    <div className="min-h-[calc(100dvh-5rem)] sm:min-h-[calc(100vh-8rem)] bg-gradient-to-br from-slate-50 via-indigo-50/40 to-violet-50/60 pb-[max(2.5rem,env(safe-area-inset-bottom))] overflow-x-hidden">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 pt-4 sm:pt-8">
        <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-6">
          <button
            type="button"
            onClick={() => setActiveView?.('generate')}
            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white/90 text-slate-700 hover:bg-white active:bg-slate-50 shrink-0 touch-manipulation"
            title="Back"
          >
            <ArrowLeft size={22} className="sm:w-5 sm:h-5" />
          </button>
          <div className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-indigo-600 text-white shadow-lg shadow-indigo-200 shrink-0">
            <Megaphone size={22} className="sm:w-6 sm:h-6" />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h1 className="text-lg sm:text-2xl font-bold text-slate-900 tracking-tight leading-tight">Promoters</h1>
            <p className="text-xs sm:text-sm text-slate-600 mt-1 sm:mt-1.5 max-w-xl leading-relaxed">
              Ask vendors to promote their fabrics or body cuts, respond to requests, and see who you approved.
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-4 gap-1 p-1 rounded-xl sm:rounded-2xl bg-white/80 border border-slate-200/80 shadow-sm mb-4 sm:mb-6"
          role="tablist"
          aria-label="Promoters sections"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = mainTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setMainTab(t.id)}
                className={`min-h-[48px] sm:min-h-[52px] min-w-0 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-1 sm:px-3 py-2 rounded-lg sm:rounded-xl text-[11px] sm:text-sm font-semibold transition-all touch-manipulation active:scale-[0.98] ${
                  active
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-100/80 active:bg-slate-100'
                }`}
              >
                <Icon size={18} className="shrink-0" aria-hidden />
                <span className="text-center leading-tight sm:truncate max-w-full">
                  <span className="sm:hidden">{t.shortLabel}</span>
                  <span className="hidden sm:inline">{t.label}</span>
                </span>
                {t.id === 'pending' && pendingCount > 0 ? (
                  <span
                    className={`text-[10px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded-full leading-none ${
                      active ? 'bg-white/20 text-white' : 'bg-indigo-100 text-indigo-700'
                    }`}
                  >
                    {pendingCount}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        {mainTab === 'send' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white/90 p-3 sm:p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-800 mb-2 sm:mb-3">Find a vendor</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                <input
                  type="search"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Name or phone…"
                  enterKeyHint="search"
                  className="w-full min-h-[48px] pl-10 pr-11 py-3 rounded-xl border border-slate-200 text-slate-900 text-base sm:text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                />
                {searchDraft ? (
                  <button
                    type="button"
                    onClick={() => setSearchDraft('')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 touch-manipulation"
                    aria-label="Clear search"
                  >
                    <X size={20} />
                  </button>
                ) : null}
              </div>
              <p className="text-[11px] sm:text-xs text-slate-500 mt-2 leading-snug">
                Verified vendors from the public directory.
              </p>
            </div>

            <div className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white/90 p-3 sm:p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-800 mb-2 sm:mb-3">Vendors</h2>
              {vendorsLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="animate-spin text-indigo-500" size={28} />
                </div>
              ) : vendors.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center px-1">No vendors match your search.</p>
              ) : (
                <ul className="divide-y divide-slate-100 max-h-[min(52vh,360px)] sm:max-h-[320px] overflow-y-auto overscroll-contain rounded-xl border border-slate-100 [-webkit-overflow-scrolling:touch]">
                  {vendors.map((v) => {
                    const id = v.id || v._id;
                    const sel = selectedVendor && String(selectedVendor.id || selectedVendor._id) === String(id);
                    return (
                      <li key={id}>
                        <button
                          type="button"
                          onClick={() => setSelectedVendor({ ...v, id })}
                          className={`w-full text-left px-3 sm:px-4 py-3.5 min-h-[52px] flex items-center gap-3 transition-colors touch-manipulation active:bg-slate-100 ${
                            sel ? 'bg-indigo-50' : 'hover:bg-slate-50'
                          }`}
                        >
                          {v.avatarUrl ? (
                            <img
                              src={v.avatarUrl}
                              alt=""
                              className="w-10 h-10 rounded-full object-cover border border-slate-200"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center text-indigo-700 font-bold text-sm">
                              {(vendorTitle(v) || '?').slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-900 truncate">{vendorTitle(v)}</p>
                            <p className="text-xs text-slate-500 truncate">{vendorSubtitle(v)}</p>
                          </div>
                          {sel ? <Check className="text-indigo-600 shrink-0" size={20} /> : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white/90 p-3 sm:p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-800 mb-2 sm:mb-3">Request type</h2>
              <div className="flex flex-col gap-2.5 sm:flex-row sm:gap-3">
                <button
                  type="button"
                  onClick={() => setPromotionType('fabrics')}
                  className={`flex-1 flex items-center gap-3 min-h-[52px] px-4 py-3 rounded-xl border-2 transition-all text-left touch-manipulation active:scale-[0.99] ${
                    promotionType === 'fabrics'
                      ? 'border-indigo-500 bg-indigo-50/80 text-indigo-900'
                      : 'border-slate-200 hover:border-slate-300 text-slate-700 active:bg-slate-50'
                  }`}
                >
                  <Layers size={22} className="shrink-0 text-indigo-600" />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">Fabrics</p>
                    <p className="text-[11px] sm:text-xs text-slate-600 leading-snug">Their fabric catalogue</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setPromotionType('body_cuts')}
                  className={`flex-1 flex items-center gap-3 min-h-[52px] px-4 py-3 rounded-xl border-2 transition-all text-left touch-manipulation active:scale-[0.99] ${
                    promotionType === 'body_cuts'
                      ? 'border-indigo-500 bg-indigo-50/80 text-indigo-900'
                      : 'border-slate-200 hover:border-slate-300 text-slate-700 active:bg-slate-50'
                  }`}
                >
                  <Shirt size={22} className="shrink-0 text-indigo-600" />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">Body cuts</p>
                    <p className="text-[11px] sm:text-xs text-slate-600 leading-snug">Garment / body-cut designs</p>
                  </div>
                </button>
              </div>

              <button
                type="button"
                onClick={handleSend}
                disabled={sendLoading || !selectedVendor}
                className="mt-4 sm:mt-5 w-full min-h-[48px] px-4 py-3 rounded-xl bg-indigo-600 text-white text-base sm:text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 touch-manipulation active:bg-indigo-800"
              >
                {sendLoading ? <Loader2 size={20} className="animate-spin" /> : <Megaphone size={20} />}
                Send promotion request
              </button>
            </div>
          </div>
        )}

        {mainTab === 'pending' && (
          <div className="space-y-6 sm:space-y-8">
            {pendingLoading ? (
              <div className="flex justify-center py-16 sm:py-20">
                <Loader2 className="animate-spin text-indigo-500" size={28} />
              </div>
            ) : (
              <>
                <section>
                  <h2 className="text-sm font-semibold text-slate-800 mb-2 sm:mb-3 flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2 sm:flex-wrap">
                    <span>Incoming</span>
                    <span className="text-xs font-normal text-slate-500">Accept or reject below</span>
                  </h2>
                  {incoming.length === 0 ? (
                    <p className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-200 bg-white/60 px-3 py-8 text-center leading-relaxed">
                      No incoming requests.
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {incoming.map((r) => (
                        <li
                          key={r.id}
                          className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white/95 p-3 sm:p-4 shadow-sm flex flex-col gap-3"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 break-words">{r.fromUser?.name || 'Vendor'}</p>
                            <p className="text-xs text-slate-500 mt-1 break-words">
                              {[r.fromUser?.phone, r.fromUser?.brandName].filter(Boolean).join(' · ')}
                            </p>
                            <p className="text-xs text-indigo-700 font-medium mt-2">{typeLabel(r.promotionType)}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-row sm:justify-end">
                            <button
                              type="button"
                              disabled={actionId === r.id}
                              onClick={() => respond(r.id, 'accepted')}
                              className="min-h-[48px] px-3 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center justify-center gap-1.5 touch-manipulation active:bg-emerald-800"
                            >
                              {actionId === r.id ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                              Accept
                            </button>
                            <button
                              type="button"
                              disabled={actionId === r.id}
                              onClick={() => respond(r.id, 'rejected')}
                              className="min-h-[48px] px-3 rounded-xl border-2 border-slate-300 text-slate-800 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50 inline-flex items-center justify-center touch-manipulation active:bg-slate-100"
                            >
                              Reject
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section>
                  <h2 className="text-sm font-semibold text-slate-800 mb-2 sm:mb-3">Outgoing</h2>
                  {outgoing.length === 0 ? (
                    <p className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-200 bg-white/60 px-3 py-8 text-center leading-relaxed">
                      You have not sent any pending requests.
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {outgoing.map((r) => (
                        <li
                          key={r.id}
                          className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white/95 p-3 sm:p-4 shadow-sm flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 break-words">To: {r.toUser?.name || 'Vendor'}</p>
                            <p className="text-xs text-slate-500 mt-1 break-all">{r.toUser?.phone}</p>
                            <p className="text-xs text-indigo-700 font-medium mt-2">{typeLabel(r.promotionType)}</p>
                          </div>
                          <span className="text-xs font-semibold text-amber-800 bg-amber-50 px-2.5 py-2 rounded-lg self-start sm:self-center shrink-0">
                            Awaiting response
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </>
            )}
          </div>
        )}

        {mainTab === 'promoters' && (
          <div>
            {promotersLoading ? (
              <div className="flex justify-center py-16 sm:py-20">
                <Loader2 className="animate-spin text-indigo-500" size={28} />
              </div>
            ) : promoters.length === 0 ? (
              <p className="text-sm text-slate-500 rounded-xl sm:rounded-2xl border border-dashed border-slate-200 bg-white/60 px-3 py-10 sm:py-12 text-center leading-relaxed">
                When you accept someone&apos;s promotion request, they will appear here.
              </p>
            ) : (
              <ul className="space-y-3">
                {promoters.map((row, idx) => {
                  const key = row.requestId || row.user?._id || row.user?.id || `promoter-${idx}`;
                  const removing = removePromoterId && String(removePromoterId) === String(row.requestId);
                  return (
                    <li
                      key={key}
                      className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white/95 p-3 sm:p-4 shadow-sm flex items-start gap-3 sm:gap-4"
                    >
                      {row.user?.avatarUrl ? (
                        <img
                          src={row.user.avatarUrl}
                          alt=""
                          className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-cover border border-slate-200 shrink-0 mt-0.5"
                        />
                      ) : (
                        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center text-indigo-700 font-bold shrink-0 text-sm sm:text-base">
                          {(row.user?.name || '?').slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0 pt-0.5">
                        <p className="font-medium text-slate-900 break-words">{row.user?.name || 'Vendor'}</p>
                        <p className="text-xs text-slate-500 mt-1 break-words">
                          {[row.user?.phone, row.user?.brandName].filter(Boolean).join(' · ')}
                        </p>
                        <p className="text-xs text-indigo-700 font-medium mt-1.5">{typeLabel(row.promotionType)}</p>
                      </div>
                      <button
                        type="button"
                        disabled={!row.requestId || removing}
                        onClick={async () => {
                          if (!row.requestId) return;
                          setRemovePromoterId(row.requestId);
                          try {
                            await removeApprovedPromoter(row.requestId);
                            showNotification?.('Promoter removed', 'success');
                            await loadPromotersList();
                          } catch (e) {
                            console.error(e);
                            showNotification?.(e.response?.data?.message || 'Could not remove promoter', 'error');
                          } finally {
                            setRemovePromoterId(null);
                          }
                        }}
                        className="min-h-[44px] px-3 rounded-xl border-2 border-rose-200 text-rose-700 bg-rose-50/60 hover:bg-rose-50 text-xs font-semibold inline-flex items-center justify-center gap-1.5 shrink-0 touch-manipulation active:bg-rose-100 disabled:opacity-50"
                        title="Remove promoter"
                      >
                        {removing ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
                        Remove
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {mainTab === 'suppliers' && (
          <div>
            {suppliersLoading ? (
              <div className="flex justify-center py-16 sm:py-20">
                <Loader2 className="animate-spin text-indigo-500" size={28} />
              </div>
            ) : suppliers.length === 0 ? (
              <p className="text-sm text-slate-500 rounded-xl sm:rounded-2xl border border-dashed border-slate-200 bg-white/60 px-3 py-10 sm:py-12 text-center leading-relaxed">
                Vendors who accept your promotion request will appear here.
              </p>
            ) : (
              <ul className="space-y-3">
                {suppliers.map((row, idx) => {
                  const u = row?.user || row;
                  const id = u?.id || u?._id;
                  const title = vendorTitle(u);
                  const subtitle = vendorSubtitle(u);
                  const key = id || `${title || 'supplier'}-${u?.phoneDigits || u?.phone || idx}`;
                  return (
                    <li
                      key={key}
                      className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white/95 p-3 sm:p-4 shadow-sm flex items-start gap-3 sm:gap-4"
                    >
                      {u?.avatarUrl ? (
                        <img
                          src={u.avatarUrl}
                          alt=""
                          className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-cover border border-slate-200 shrink-0 mt-0.5"
                        />
                      ) : (
                        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center text-indigo-700 font-bold shrink-0 text-sm sm:text-base">
                          {(title || '?').slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0 pt-0.5">
                        <p className="font-medium text-slate-900 break-words">{title}</p>
                        <p className="text-xs text-slate-500 mt-1 break-words">{subtitle}</p>
                        {Array.isArray(row?.promotionTypes) && row.promotionTypes.length > 0 ? (
                          <p className="text-xs text-indigo-700 font-medium mt-1.5">
                            {row.promotionTypes.map(typeLabel).join(' · ')}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotersView;
